import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button, ButtonGroup, UncontrolledTooltip } from 'reactstrap';
import { CalibrationRecommendation } from '../types';
import {
  getRecommendationDetails,
  RECOMMENDATION_FILTER_ORDER,
} from '../utils/comments';

const shouldDisplayRecommendation = (
  recommendation: CalibrationRecommendation
): boolean => RECOMMENDATION_FILTER_ORDER.includes(recommendation);

export interface RecommendationInputProps {
  value?: CalibrationRecommendation;
  onChange: (value?: CalibrationRecommendation) => void;
  onValidChange: (message: string | null) => void;
  disabled?: boolean;
}

export const RecommendationInput: FC<RecommendationInputProps> = ({
  value,
  onChange,
  onValidChange,
  disabled,
}) => {
  const { formatMessage } = useIntl();
  // Clear validation on mount since this field has no validation requirements
  React.useEffect(() => {
    onValidChange(null);
  }, [onValidChange]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const recommendation = event.currentTarget.dataset.recommendation as
        | CalibrationRecommendation
        | undefined;
      onChange(recommendation || undefined);
    },
    [onChange]
  );

  return (
    <ButtonGroup
      size="sm"
      aria-label={formatMessage({
        id: 'app.views.widgets.dashboards.calibration_dashboard.components.calibration_comments_popover_content.aria-label.calibration_recommendation_toggle',
        defaultMessage: 'Calibration recommendation toggle',
      })}
    >
      {[undefined, ...RECOMMENDATION_FILTER_ORDER].map((option) => {
        if (option && !shouldDisplayRecommendation(option)) {
          return null;
        }
        const active = value === option;
        const { icon, label, tooltip } = getRecommendationDetails(
          formatMessage,
          option
        );
        const buttonId = `calibration-recommendation-${option ?? 'none'}`;
        return (
          <React.Fragment key={option ?? 'none'}>
            <Button
              id={buttonId}
              color="primary"
              outline={!active}
              active={active}
              onClick={handleClick}
              data-recommendation={option ?? ''}
              type="button"
              disabled={disabled}
            >
              {icon ??
                formatMessage({
                  id: 'app.views.widgets.dashboards.calibration_dashboard.comments.recommendation.none_short',
                  defaultMessage: 'None',
                })}
              <span className="visually-hidden">{label}</span>
            </Button>
            <UncontrolledTooltip target={buttonId}>
              {tooltip}
            </UncontrolledTooltip>
          </React.Fragment>
        );
      })}
    </ButtonGroup>
  );
};
